import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { PROVINCES, money, transitCopy } from "@/lib/format";
import { Alert, Button, Card, Field, Select, Spinner, cx } from "@/components/ui";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/state/auth";

/**
 * The public landing page.
 *
 * Two things on it actually work rather than describing work: the rate
 * estimator calls the live carrier, and the tracking box goes to the real
 * tracking page. Everything else is a claim, and the rule applied throughout is
 * that a claim only appears if the platform can currently back it. No invented
 * shipment counts, no guarantee amounts nobody has underwritten, and no
 * integration logos for integrations that do not exist yet.
 */
export function Landing() {
  const { account } = useAuth();

  return (
    <div className="min-h-dvh bg-white">
      <SiteHeader signedIn={Boolean(account)} />
      <Hero signedIn={Boolean(account)} />
      <SocialProof />
      <TrackSection />
      <TrustStrip />
      <Services />
      <HowItWorks />
      <ForDevelopers />
      <Wallet />
      <Comparison />
      <FinalCall signedIn={Boolean(account)} />
      <SiteFooter />
    </div>
  );
}

function SiteHeader({ signedIn }: { signedIn: boolean }) {
  return (
    <header className="sticky top-0 z-20 border-b border-ink-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-6 px-4 py-3 lg:px-8">
        <Link to="/" className="flex items-center gap-2 text-lg font-bold tracking-tight">
          <LogoMark className="h-8 w-8" />
          WeShip
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {[
            ["Services", "#services"],
            ["How it works", "#how"],
            ["Developers", "#developers"],
            ["Pricing", "#estimate"],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-600 transition hover:bg-ink-100 hover:text-ink-900"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link to="/track" className="hidden text-sm font-semibold text-ink-600 hover:text-ink-900 sm:block">
            Track a parcel
          </Link>
          {signedIn ? (
            <Link to="/app">
              <Button size="sm">Go to dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button size="sm" variant="ghost">
                  Sign in
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm">Create account</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="border-b border-ink-200 bg-ink-50">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 lg:grid-cols-2 lg:items-start lg:px-8 lg:py-24">
        <div>
          <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight text-ink-950 lg:text-5xl">
            <span className="text-brand">We</span>
            <span className="text-accent">Ship</span> what other couriers don't.
          </h1>
          <p className="mt-5 max-w-lg text-lg text-ink-600">
            See the price before you commit, book in under a minute, and follow the parcel to the
            door. One account, one waybill, one place to look.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/register">
              <Button>Create an account</Button>
            </Link>
            {/* Straight into booking, not the quote card: someone who has
                already signed in or is happy to book as a guest does not need
                to be routed through the estimator first. */}
            <Link to={signedIn ? "/app/book" : "/ship"}>
              <Button variant="secondary">Book a shipment</Button>
            </Link>
          </div>

          <dl className="mt-10 grid max-w-md grid-cols-3 gap-6 border-t border-ink-200 pt-8">
            {[
              ["Coverage", "Nationwide"],
              ["Same day", "Metro"],
              ["Pricing", "Live, per parcel"],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-ink-500">{label}</dt>
                <dd className="mt-1 font-bold tracking-tight">{value}</dd>
              </div>
            ))}
          </dl>

          <div id="estimate" className="mt-8 max-w-md">
            <QuoteBar />
          </div>
        </div>

        <img
          src="/marketing/fleet-loaded.jpg"
          alt="WeShip vehicles loaded and ready for the day's collections"
          className="aspect-[4/3] w-full rounded-2xl object-cover shadow-pop"
        />
      </div>
    </section>
  );
}

interface PublicOption {
  service: string;
  service_level: string;
  price_cents: number;
  vat_cents: number;
  transit_days: number;
}

/**
 * The estimator is the whole argument of the page: prices before signup. It
 * calls the same carrier the booking flow uses, so what it shows is what a new
 * account would pay, not an illustration.
 */
function RateEstimator({ bare = false }: { bare?: boolean }) {
  const navigate = useNavigate();
  const [from, setFrom] = useState({ city: "Pretoria", province: "Gauteng", postal_code: "0181", suburb: "" });
  const [to, setTo] = useState({ city: "Cape Town", province: "Western Cape", postal_code: "8005", suburb: "" });
  const [weight, setWeight] = useState("2");
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  const quote = useMutation({
    mutationFn: () =>
      api.post<{ options: PublicOption[]; notice: string }>("/v1/public/quote", {
        collection: from,
        delivery: to,
        parcel: {
          length_mm: 300,
          width_mm: 200,
          height_mm: 150,
          weight_g: Math.max(1, Math.round(Number(weight) * 1000)),
        },
      }),
    onMutate: () => {
      setError("");
      setFields({});
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (Object.keys(err.fields).length === 0) setError(err.message);
      } else {
        setError("We could not price that right now. Try again in a moment.");
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    quote.mutate();
  }

  const content = (
    <>
      {!bare && (
        <>
          <h2 className="text-lg font-bold tracking-tight">What will it cost?</h2>
          <p className="mb-5 mt-1 text-sm text-ink-500">
            Live carrier pricing. No account needed to look.
          </p>
        </>
      )}

      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="From city"
            value={from.city}
            onChange={(e) => setFrom({ ...from, city: e.target.value })}
            error={fields["collection.city"]}
          />
          <Field
            label="Postal code"
            inputMode="numeric"
            maxLength={4}
            value={from.postal_code}
            onChange={(e) => setFrom({ ...from, postal_code: e.target.value })}
            error={fields["collection.postal_code"]}
          />
          <Select
            label="Province"
            className="col-span-2"
            value={from.province}
            onChange={(e) => setFrom({ ...from, province: e.target.value })}
            error={fields["collection.province"]}
            options={PROVINCES.map((p) => ({ value: p, label: p }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 border-t border-ink-200 pt-4">
          <Field
            label="To city"
            value={to.city}
            onChange={(e) => setTo({ ...to, city: e.target.value })}
            error={fields["delivery.city"]}
          />
          <Field
            label="Postal code"
            inputMode="numeric"
            maxLength={4}
            value={to.postal_code}
            onChange={(e) => setTo({ ...to, postal_code: e.target.value })}
            error={fields["delivery.postal_code"]}
          />
          <Select
            label="Province"
            className="col-span-2"
            value={to.province}
            onChange={(e) => setTo({ ...to, province: e.target.value })}
            error={fields["delivery.province"]}
            options={PROVINCES.map((p) => ({ value: p, label: p }))}
          />
        </div>

        <Field
          label="Parcel weight (kg)"
          type="number"
          min={0.1}
          step={0.5}
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          error={fields.weight_g}
          hint="Based on a 30 x 20 x 15 cm box. Exact dimensions when you book."
        />

        <Button type="submit" className="w-full" loading={quote.isPending}>
          Show prices
        </Button>
      </form>

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {quote.isPending && (
        <p className="mt-4 flex items-center gap-2 text-sm text-ink-500">
          <Spinner /> Asking the carrier
        </p>
      )}

      {quote.data && (
        <div className="mt-5 space-y-2">
          {quote.data.options.map((option) => (
            <div
              key={option.service_level}
              className="flex items-center justify-between rounded-xl border border-ink-200 px-4 py-3"
            >
              <div>
                <p className="font-semibold">{option.service}</p>
                <p className="text-xs text-ink-500">{transitCopy(option.transit_days)}</p>
              </div>
              <p className="text-lg font-bold tabular-nums">{money(option.price_cents)}</p>
            </div>
          ))}
          <p className="pt-1 text-xs text-ink-500">{quote.data.notice}</p>
          <Button className="w-full" onClick={() => navigate("/register")}>
            Create an account to book
          </Button>
          <Button variant="secondary" className="w-full" onClick={() => navigate("/ship")}>
            Or book this once, no account needed
          </Button>
        </div>
      )}
    </>
  );

  if (bare) return content;
  return <Card className="shadow-pop">{content}</Card>;
}

/**
 * Collapsed by default: a compact bar that states the offer in one line
 * rather than opening straight into a six-field form. Expanding is a state
 * toggle animated with a grid-template-rows transition, not a route change,
 * so the estimator underneath never remounts and a route already typed in
 * is not lost by opening and closing this. Lives directly in the hero's
 * empty corner rather than its own section further down: getting a price
 * is the thing the whole page is arguing you should do first.
 */
function QuoteBar() {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="shadow-pop">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-4 text-left"
        aria-expanded={expanded}
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <TruckIcon />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold tracking-tight">Get a delivery quote</span>
          <span className="block text-sm text-ink-500">
            Enter your route and parcel details to see live prices.
          </span>
        </span>
        <span
          className={cx(
            "shrink-0 text-sm font-semibold text-brand transition-transform",
            expanded && "rotate-90",
          )}
          aria-hidden="true"
        >
          &rarr;
        </span>
      </button>

      <div
        className={cx(
          "grid transition-[grid-template-rows] duration-300 ease-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-6 border-t border-ink-200 pt-6">
            <RateEstimator bare />
          </div>
        </div>
      </div>
    </Card>
  );
}

function TruckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
      <path
        d="M2 6a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v10H3a1 1 0 0 1-1-1V6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M13 10h3.5a1 1 0 0 1 .8.4l2.3 3a1 1 0 0 1 .2.6V15a1 1 0 0 1-1 1H13v-6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="17" r="1.6" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="17" r="1.6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/**
 * Tracking gets its own stage rather than sharing a row with the trust
 * bullets below: someone arriving to check on a parcel already in transit
 * is doing something different from someone still deciding whether to book,
 * and deserves to find the box without hunting for it.
 */
function TrackSection() {
  return (
    <section className="border-b border-ink-200 bg-ink-950 text-white">
      <div className="mx-auto max-w-6xl px-4 py-12 text-center lg:px-8">
        <h2 className="text-2xl font-extrabold tracking-tight">Already shipped with us?</h2>
        <p className="mt-2 text-ink-300">Track any waybill. No login needed.</p>
        <div className="mt-6 flex justify-center">
          <TrackBox />
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="border-b border-ink-200">
      <div className="mx-auto max-w-6xl px-4 py-8 lg:px-8">
        <ul className="grid gap-x-8 gap-y-3 text-sm text-ink-600 sm:grid-cols-2 lg:grid-cols-4">
          {[
            "Prices include VAT before you book",
            "Prepaid wallet, no monthly minimum",
            "Live tracking on a link you can share",
            "Cancel before collection, refunded automatically",
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              {line}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Real vehicles, real handoffs -- not stock photography. Each photo backs one
 * specific claim rather than standing in generically for "trust us": the
 * insulated crates are what "ships what other couriers won't" actually looks
 * like, not just a line in the hero.
 */
function SocialProof() {
  const items = [
    {
      src: "/marketing/cold-chain.jpg",
      alt: "Temperature-controlled crates loaded for a delivery run",
      caption: "We ship what other couriers turn away",
      body: "Temperature-sensitive and delicate cargo, packed and moved the way it actually needs to be.",
    },
    {
      src: "/marketing/handoff.jpg",
      alt: "A WeShip driver handing a parcel to a customer at her door",
      caption: "Handed over, not just dropped off",
      body: "A real handoff at the door, every time, from a driver your customer can see and trust.",
    },
    {
      src: "/marketing/careful-loading.jpg",
      alt: "A WeShip driver carefully loading boxes into a vehicle",
      caption: "Drivers trained to handle it with care",
      body: "Trained on how to pack, carry and load, so precious and fragile goods arrive the way they left.",
    },
  ];

  return (
    <section className="border-b border-ink-200">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
        <h2 className="text-3xl font-extrabold tracking-tight">Not a stock photo in sight.</h2>
        <p className="mt-2 max-w-xl text-ink-600">Our own vehicles, our own drivers, our own deliveries.</p>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {items.map((item) => (
            <div key={item.src}>
              <img
                src={item.src}
                alt={item.alt}
                className="aspect-[4/5] w-full rounded-2xl object-cover shadow-pop"
              />
              <h3 className="mt-4 font-bold tracking-tight">{item.caption}</h3>
              <p className="mt-1 text-sm text-ink-600">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrackBox() {
  const navigate = useNavigate();
  const [waybill, setWaybill] = useState("");

  return (
    <form
      className="flex w-full max-w-sm gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (waybill.trim()) navigate(`/track/${encodeURIComponent(waybill.trim())}`);
      }}
    >
      <input
        value={waybill}
        onChange={(event) => setWaybill(event.target.value.toUpperCase())}
        placeholder="Track a waybill"
        aria-label="Waybill number"
        className="ws-input font-mono"
      />
      <Button type="submit" variant="secondary">
        Track
      </Button>
    </form>
  );
}

function Services() {
  const services = [
    {
      name: "Same day",
      body: "Collected and delivered inside one metro on the same working day, booked before the carrier cut-off.",
      note: "Metro routes",
    },
    {
      name: "Overnight express",
      body: "Collected today, delivered the next working day. The default for anything that has to arrive tomorrow.",
      note: "Nationwide",
    },
    {
      name: "Economy",
      body: "The cheapest scheduled service. Slower, handled the same way, and priced accordingly.",
      note: "Nationwide",
    },
  ];

  return (
    <section id="services" className="border-b border-ink-200 bg-ink-50">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
        <h2 className="text-3xl font-extrabold tracking-tight">Choose the speed that fits.</h2>
        <p className="mt-2 max-w-xl text-ink-600">
          Every service is priced live for your exact parcel and route. Availability depends on the
          addresses, so what you see quoted is what is actually collectable.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {services.map((service) => (
            <Card key={service.name}>
              <span className="text-2xs font-bold uppercase text-brand">{service.note}</span>
              <h3 className="mt-2 text-xl font-bold tracking-tight">{service.name}</h3>
              <p className="mt-2 text-sm text-ink-600">{service.body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    ["Get a price", "Enter the two addresses and the parcel. Every available service comes back with a price including VAT."],
    ["Book it", "Pick a service and confirm. Your waybill is issued immediately and the collection goes to the courier."],
    ["Track it", "Follow it in the portal, or send your customer the tracking link. No login needed on their side."],
    ["Delivered", "Proof of delivery lands against the shipment. Cancel before collection and the wallet is refunded."],
  ];

  return (
    <section id="how" className="border-b border-ink-200">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
        <h2 className="text-3xl font-extrabold tracking-tight">From booking to door, in four steps.</h2>
        <ol className="mt-8 grid gap-6 md:grid-cols-4">
          {steps.map(([title, body], index) => (
            <li key={title}>
              <span className="font-mono text-sm font-bold text-brand">0{index + 1}</span>
              <h3 className="mt-2 font-bold tracking-tight">{title}</h3>
              <p className="mt-1.5 text-sm text-ink-600">{body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function ForDevelopers() {
  return (
    <section id="developers" className="border-b border-ink-200 bg-ink-950 text-white">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 lg:grid-cols-2 lg:items-center lg:px-8">
        <div>
          <span className="text-2xs font-bold uppercase text-accent">For developers</span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight">
            Ship straight from your own systems.
          </h2>
          <p className="mt-4 text-ink-300">
            A REST API with server-side keys, rate quoting, booking and tracking. Test keys run the
            same validation and pricing against a sandbox, so you can build the whole integration
            before a single parcel moves.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-ink-300">
            {[
              "Idempotency keys, so a retry never books twice",
              "Separate test and live keys, revocable at any time",
              "Every rejection comes back as a field and a message",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                {line}
              </li>
            ))}
          </ul>
        </div>

        <pre className="overflow-x-auto rounded-2xl bg-ink-900 p-5 text-xs leading-relaxed text-ink-200">
{`curl -X POST https://app.weship.co.za/v1/quotes \\
  -H "X-API-Key: wsk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "delivery": {
      "street": "5 Long Street", "suburb": "Sea Point",
      "city": "Cape Town", "province": "Western Cape",
      "postal_code": "8005", "contact_name": "Sam",
      "phone": "0839876543"
    },
    "parcels": [{
      "length_mm": 300, "width_mm": 200,
      "height_mm": 150, "weight_g": 2000
    }]
  }'`}
        </pre>
      </div>
    </section>
  );
}

function Wallet() {
  return (
    <section className="border-b border-ink-200">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 lg:grid-cols-2 lg:items-center lg:px-8">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight">Pay as you ship.</h2>
          <p className="mt-4 text-ink-600">
            Top up your wallet by card or instant EFT. Each booking draws from the balance, and every
            movement appears on a statement you can read: what it was for, which waybill, and the
            balance it left behind.
          </p>
          <p className="mt-4 text-ink-600">
            Cancel a shipment before it is collected and the exact amount charged goes back, without
            anyone having to ask.
          </p>
          <Link to="/register" className="mt-6 inline-block">
            <Button>Open an account</Button>
          </Link>
        </div>

        <Card>
          <div className="flex items-baseline justify-between">
            <p className="text-sm text-ink-500">Wallet balance</p>
            <p className="text-2xl font-bold tracking-tight">R 1,250.00</p>
          </div>
          <ul className="mt-4 divide-y divide-ink-100 text-sm">
            {[
              ["Top-up", "+R 500.00", "text-signal-good"],
              ["Overnight Express to Cape Town", "-R 198.00", ""],
              ["Shipment refunded", "+R 198.00", "text-signal-good"],
            ].map(([label, amount, tone]) => (
              <li key={label} className="flex items-center justify-between gap-4 py-2.5">
                <span className="text-ink-600">{label}</span>
                <span className={cx("font-semibold tabular-nums", tone)}>{amount}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-ink-400">Illustration of the statement view.</p>
        </Card>
      </div>
    </section>
  );
}

function Comparison() {
  const rows = [
    ["Seeing the price first", "Quoted live for your parcel, VAT included", "Rate card, worked out afterwards"],
    ["Booking", "A minute in the portal or one API call", "Email or a phone call"],
    ["Tracking", "A link your customer opens without logging in", "A reference and a phone number"],
    ["Paying", "Prepaid wallet with a readable statement", "Monthly invoice, reconciled by hand"],
    ["Cancelling", "Refunded to the wallet automatically", "A credit note, eventually"],
    ["Team access", "Roles, down to view-only without billing", "One shared login"],
  ];

  return (
    <section className="border-b border-ink-200 bg-ink-50">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
        <h2 className="text-3xl font-extrabold tracking-tight">Built for how businesses ship now.</h2>
        <div className="ws-card mt-8 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-200 bg-white">
              <tr>
                <th className="ws-th">&nbsp;</th>
                <th className="ws-th text-brand">WeShip</th>
                <th className="ws-th">The usual way</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 bg-white">
              {rows.map(([label, ours, theirs]) => (
                <tr key={label}>
                  <td className="ws-td font-semibold">{label}</td>
                  <td className="ws-td">{ours}</td>
                  <td className="ws-td text-ink-500">{theirs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function FinalCall({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="bg-ink-950 text-white">
      <div className="mx-auto max-w-3xl px-4 py-20 text-center lg:px-8">
        <h2 className="text-3xl font-extrabold tracking-tight lg:text-4xl">
          Ready to move your deliveries forward?
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-ink-300">
          Open an account in two minutes. There is no monthly fee and no minimum: you pay for the
          parcels you send.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to={signedIn ? "/app" : "/register"}>
            <Button>{signedIn ? "Go to dashboard" : "Create an account"}</Button>
          </Link>
          <Link to={signedIn ? "/app/book" : "/ship"}>
            <Button variant="secondary">Book a shipment</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-ink-200 bg-white">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-8 lg:px-8">
        <div className="flex items-center gap-2 font-bold tracking-tight">
          <LogoMark className="h-7 w-7" />
          WeShip
        </div>
        <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-600">
          <Link to="/track">Track a parcel</Link>
          <Link to="/login">Sign in</Link>
          <Link to="/register">Create account</Link>
          <a href="#developers">Developers</a>
        </nav>
        <p className="text-xs text-ink-400">
          &copy; {new Date().getFullYear()} WeShip. Prices include VAT.
        </p>
      </div>
    </footer>
  );
}
